// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Body1, Button, Image, Title3, makeStyles, tokens } from '@fluentui/react-components';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AbtisConfig } from '../../abtis-config';
import signInLogoDark from '../../ms-symbollockup_signin_dark.svg';
import signInLogo from '../../ms-symbollockup_signin_light.svg';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { useSharedClasses } from '../../styles';
import { getErrorDetails } from '../utils/TextUtils';

const useClasses = makeStyles({
    link: {
        color: tokens.colorNeutralForeground1,
    },
});

export const Login: React.FC = () => {
    const classes = useClasses();
    const sharedClasses = useSharedClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { instance } = useMsal();
    const { t } = useTranslation();
    const imageRef = useRef<HTMLImageElement>(null); // Initialize the ref with null

    useEffect(() => {
        if (features[FeatureKeys.DarkMode].enabled) {
            imageRef.current?.setAttribute('src', signInLogoDark);
        }
    }, [features]);

    return (
        <div className={sharedClasses.informativeView}>
            {!AbtisConfig.featureFlags.showAbtisLoginScreen ? (
                <>
                    <Title3>Login with your Microsoft Account</Title3>
                    <Body1>
                        {"Don't have an account? Create one for free at"}{' '}
                        <a href="https://account.microsoft.com/" target="_blank" rel="noreferrer">
                            https://account.microsoft.com/
                        </a>
                    </Body1>
                    <Button
                        style={{ padding: 0 }}
                        appearance="transparent"
                        onClick={() => {
                            instance.loginRedirect().catch((e: unknown) => {
                                alert(`Error signing in: ${getErrorDetails(e)}`);
                            });
                        }}
                    >
                        <Image src={signInLogo} />
                    </Button>
                </>
            ) : (
                <>
                    <Title3>{t('loginIntoMicrosoftAccount')}</Title3>
                    <Body1>
                        {t('createMicrosoftAccountHint')}{' '}
                        <a
                            className={classes.link}
                            href="https://account.microsoft.com/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            https://account.microsoft.com/
                        </a>
                    </Body1>
                    <Button
                        style={{ padding: 0 }}
                        appearance="transparent"
                        onClick={() => {
                            instance.loginRedirect().catch((e: unknown) => {
                                alert(`Error signing in: ${getErrorDetails(e)}`);
                            });
                        }}
                    >
                        <Image ref={imageRef} src={signInLogo} />
                    </Button>
                </>
            )}
        </div>
    );
};
